import React, { useState } from "react";
import {
  VStack,
  Heading,
  Button,
  Input,
  CardRoot,
  CardBody,
  Field,
  Box,
  Text,
  HStack,
  Link,
} from "@chakra-ui/react";
import { useColorModeValue } from "./ui/color-mode";
import navigationSound from "../assets/sounds/navigation.mp3";
import { useAccessibility } from "../context/AccessibilityContext";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase/firebase";
import { toaster } from "./ui/toaster";

const LoginComponent = ({ onRegisterClick, onForgotPasswordClick }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const { soundEnabled } = useAccessibility();

  const playSound = (sound) => {
    if (soundEnabled) {
      new Audio(sound).play();
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLogin = async () => {
    setError("");

    // Validation
    if (!username.trim()) {
      setError("Please enter your username");
      return;
    }

    if (!password) {
      setError("Please enter your password");
      return;
    }

    setIsSubmitting(true);
    try {
      playSound(navigationSound);

      // Note: Firebase uses email for login, but UI says "Username". 
      // Assuming users might enter email in the username field or we need to look up email. 
      // For now, passing 'username' value as email to signInWithEmailAndPassword since standard firebase auth uses email.
      // If username lookup is required, it's a different flow. 
      // Given the field is `autoComplete="username"`, but variable is `username`, I will assume the input is treated as email if it looks like one, or we might need clarification.
      // For a standard "Login", usually email is expected unless we have a username->email mapping.
      // Let's assume input is email for now or try to use it directly.

      await signInWithEmailAndPassword(auth, username, password);

      setIsSubmitting(false);
      toaster.create({
        title: "Login successful",
        description: `Welcome back!`,
        type: "success",
      });
      navigate("/home", { state: { screen: 1 } });

    } catch (err) {
      setIsSubmitting(false);
      console.error("Login error:", err);
      // Map some common firebase errors to user friendly messages
      let msg = "Failed to login. Please check your credentials.";
      if (err.code === 'auth/invalid-credential') msg = "Invalid username/email or password.";
      if (err.code === 'auth/user-not-found') msg = "Account not found.";
      if (err.code === 'auth/wrong-password') msg = "Invalid password.";

      toaster.create({
        title: "Login failed",
        description: msg,
        type: "error",
      });
      setError(msg);
    }
  };

  const handleRegisterClick = () => {
    playSound(navigationSound);
    if (onRegisterClick) {
      onRegisterClick();
    }
  };

  const handleForgotPasswordClick = () => {
    playSound(navigationSound);
    if (onForgotPasswordClick) {
      onForgotPasswordClick();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  const cardBg = useColorModeValue("white", "gray.800");
  const headingColor = useColorModeValue("gray.700", "white");
  const errorColor = useColorModeValue("red.600", "red.400");
  const errorBg = useColorModeValue("red.50", "red.900");
  const linkColor = useColorModeValue("blue.600", "blue.400");
  const textColor = useColorModeValue("gray.600", "gray.300");

  return (
    <Box width="100%" maxW="md" mx="auto" mt={10} mb={10}>
      <CardRoot boxShadow="xl" borderRadius="xl" bg={cardBg}>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <Heading
              as="h2"
              size="xl"
              textAlign="center"
              color={headingColor}
              mb={4}
            >
              Login
            </Heading>

            {error && (
              <Box
                bg={errorBg}
                color={errorColor}
                p={3}
                borderRadius="md"
                fontSize="sm"
              >
                {error}
              </Box>
            )}

            {/* Username Field */}
            <Field.Root>
              <Field.Label fontSize="lg" fontWeight="bold" mb={2}>
                Username
              </Field.Label>
              <Input
                autoFocus
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyPress={handleKeyPress}
                size="lg"
                autoComplete="username"
              />
            </Field.Root>

            {/* Password Field */}
            <Field.Root>
              <Field.Label fontSize="lg" fontWeight="bold" mb={2}>
                Password
              </Field.Label>
              <Input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                size="lg"
                autoComplete="current-password"
              />
            </Field.Root>

            {/* Forgot Password Link */}
            <Box textAlign="right">
              <Link
                color={linkColor}
                fontSize="sm"
                fontWeight="medium"
                onClick={handleForgotPasswordClick}
                cursor="pointer"
                _hover={{ textDecoration: "underline" }}
              >
                Forgot Password?
              </Link>
            </Box>

            {/* Login Button */}
            <Button
              colorScheme="blue"
              size="lg"
              width="full"
              onClick={handleLogin}
              mt={2}
              loading={isSubmitting}
            >
              Login
            </Button>

            {/* Register Link */}
            <Box textAlign="center" mt={4}>
              <HStack justifyContent="center" spacing={2}>
                <Text fontSize="sm" color={textColor}>
                  Don't have an account?
                </Text>
                <Link
                  color={linkColor}
                  fontSize="sm"
                  fontWeight="bold"
                  onClick={handleRegisterClick}
                  cursor="pointer"
                  _hover={{ textDecoration: "underline" }}
                >
                  Register Now
                </Link>
              </HStack>
            </Box>
          </VStack>
        </CardBody>
      </CardRoot>
    </Box>
  );
};

export default LoginComponent;
